/**
 * TodayViewCanvas — pure presentational composition for the kanban.
 *
 * Per FWV v8 §30: rendering extracted from TodayView into a separate file
 * so the stateful component (index.tsx) stays focused on state + effects.
 * This component receives everything it needs via props.
 */
import {
  DndContext,
  DragOverlay,
  type DragEndEvent,
  type DragStartEvent,
  type DragMoveEvent,
  type DragCancelEvent,
} from '@dnd-kit/core'
import { useSensors, useSensor, PointerSensor } from '@dnd-kit/core'
import type { DbTask } from '../../db'
import type { Rect } from './useRectangleDrag'
import { KanbanColumn } from './KanbanColumn'
import { SelectionActionBar } from './SelectionActionBar'
import { DragOverlayCard } from './DragOverlayCard'

export const COLUMNS: { id: DbTask['status']; label: string; tone: string }[] = [
  { id: 'todo', label: 'To do', tone: 'border-slate-200' },
  { id: 'in_progress', label: 'Doing', tone: 'border-amber-200' },
  { id: 'pending_approval', label: 'Done — needs review', tone: 'border-sky-200' },
  { id: 'done', label: 'Done', tone: 'border-emerald-200' },
]

export type ColumnTaskMap = Record<DbTask['status'], DbTask[]>

export function TodayViewCanvas({
  tasksByStatus,
  hasSelection,
  activeDragId,
  dragCursorY,
  rect,
  selectionCount,
  activeTask,
  filterAssignee,
  currentMember,
  onContainerRef,
  onCardClick,
  onCardAdvance,
  onInsertionChangeByStatus,
  onDragStart,
  onDragMove,
  onDragEnd,
  onDragCancel,
  onActionBarMove,
  onClearSelection,
  onSelectFromRectangle,
  onSetFilterAssignee,
}: {
  tasksByStatus: ColumnTaskMap
  hasSelection: (id: string) => boolean
  activeDragId: string | null
  dragCursorY: number | null
  rect: Rect
  selectionCount: number
  activeTask: DbTask | null
  filterAssignee: string | null
  currentMember: { id: string; name: string } | null
  onContainerRef: (node: HTMLDivElement | null) => void
  onCardClick: (e: React.MouseEvent, task: DbTask) => void
  onCardAdvance: (task: DbTask) => void
  onInsertionChangeByStatus: (status: DbTask['status']) => (idx: number | null) => void
  onDragStart: (e: DragStartEvent) => void
  onDragMove: (e: DragMoveEvent) => void
  onDragEnd: (e: DragEndEvent) => void
  onDragCancel: (e: DragCancelEvent) => void
  onActionBarMove: (target: DbTask['status']) => void
  onClearSelection: () => void
  onSelectFromRectangle: (ids: string[]) => void
  onSetFilterAssignee: (id: string | null) => void
}) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))

  return (
    <DndContext
      sensors={sensors}
      onDragStart={onDragStart}
      onDragMove={onDragMove}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
    >
      <div>
        <header className="mb-4">
          <h1 className="text-2xl font-semibold text-skin-text">Today</h1>
          <p className="text-slate-500 mt-1">
            Click a card to open it · shift-click to multi-select · drag onto a column to move
          </p>
        </header>

        {/* My Stuff filter — lets one family member focus on their own tasks */}
        <div className="flex items-center gap-1 mb-4 flex-wrap" data-testid="mystuff-filter">
          <button
            onClick={() => onSetFilterAssignee(null)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium min-h-touch ${
              !filterAssignee ? 'bg-garden-800 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            Everyone
          </button>
          {currentMember && (
            <button
              onClick={() => onSetFilterAssignee(currentMember.id)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium min-h-touch ${
                filterAssignee === currentMember.id ? 'bg-garden-800 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              My stuff
            </button>
          )}
          {filterAssignee && (
            <span className="text-xs text-slate-500 ml-2">filtered to {filterAssignee.replace(/^dev-/, '')}</span>
          )}
        </div>

        <div
          ref={onContainerRef}
          data-rubber-band-root
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
        >
          {COLUMNS.map((col) => {
            const colTasks = tasksByStatus[col.id]
            return (
              <KanbanColumn
                key={col.id}
                status={col.id}
                label={col.label}
                tone={col.tone}
                count={colTasks.length}
                tasks={colTasks}
                isSelected={hasSelection}
                activeDragId={activeDragId}
                dragCursorY={dragCursorY}
                onCardClick={onCardClick}
                onCardAdvance={onCardAdvance}
                onInsertionChange={onInsertionChangeByStatus(col.id)}
              />
            )
          })}

          {rect && (rect.w > 0 || rect.h > 0) && (
            <div
              className="fixed pointer-events-none z-50 rounded-md border-2 border-garden-600"
              style={{
                left: rect.x - 1, top: rect.y - 1,
                width: rect.w, height: rect.h,
                background: 'linear-gradient(135deg, rgba(109,40,217,0.10), rgba(22,163,74,0.10))',
                boxShadow: '0 0 0 1px rgba(255,255,255,0.6) inset',
              }}
            />
          )}

          {selectionCount > 0 && (
            <SelectionActionBar
              count={selectionCount}
              multiHint="or drag a selected card onto a column"
              columns={COLUMNS}
              onMove={onActionBarMove}
              onClear={onClearSelection}
            />
          )}
        </div>
      </div>

      <DragOverlay dropAnimation={null}>
        {activeTask && <DragOverlayCard task={activeTask} multiCount={selectionCount} />}
      </DragOverlay>
    </DndContext>
  )
}